import { ReactNode } from 'react';

import { classNames } from '@bytel/product-wall/helpers';
import {
    Align,
    Box,
    BoxContent,
    DirectionEnum,
    FlexBox,
    Icon,
    IconName,
    IconSize,
    Justify,
    Popover,
    PopoverDirection,
    Switch,
    Text,
    TextLevels,
    TextMarkup,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import { BigIcon, BigIconVariant } from '@components/big';

import { MarketingsState, PartialMarketingsState } from '@hooks/use-marketings';

import { MarketingType } from '@constants/plan';

import { sendPromoConfiguratorTracking } from '@home-fai/helpers';
import { SwitchChangeEvent } from '@trilogy-ds/react/lib/components/switch/SwitchProps';

interface PromoConfiguratorProps {
    title: string;
    description: string;
    removeRadius?: boolean;
    isFullwidth?: boolean;
    isChecked: boolean;
    onChange: (category: SwitchChangeEvent) => void;
    disabled?: boolean;
    icon?: ReactNode;
}

export function PromoConfigurator({
    title,
    description,
    removeRadius = false,
    isFullwidth = false,
    isChecked,
    onChange,
    disabled,
    icon,
    ...props
}: Readonly<PromoConfiguratorProps>) {
    return (
        <Box
            className={classNames(
                'promo-configurator',
                removeRadius ? 'is-radiusless' : '',
                isFullwidth ? 'is-fullwidth' : 'w-fit',
            )}
            {...props}
        >
            <BoxContent>
                <FlexBox justify={Justify.CENTER}>
                    <FlexBox
                        direction={{ mobile: DirectionEnum.COLUMN_REVERSE, tablet: DirectionEnum.ROW }}
                        align={Align.CENTER}
                    >
                        <View flexable className='has-custom-big-background big-icon w-fit'>
                            <BigIcon
                                height={16}
                                variant={BigIconVariant.INFO}
                                style={{
                                    opacity: isChecked ? '100%' : '30%',
                                    filter: isChecked ? 'none' : 'grayscale(100%)',
                                    transition: 'ease-in .1s',
                                }}
                            />
                        </View>
                        <Switch
                            id='promo-configurator' // Trilogy add a prefix 'switch-'
                            className='centered-switch'
                            checked={isChecked}
                            onChange={onChange}
                            disabled={disabled}
                        />
                    </FlexBox>
                    <FlexBox justify={Justify.CENTER} align={Align.CENTER}>
                        <label htmlFor='switch-promo-configurator'>
                            <Text level={TextLevels.TWO} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD} marginless>
                                {title}
                            </Text>
                            <Text level={TextLevels.TWO} marginless markup={TextMarkup.SPAN}>
                                {description}
                            </Text>
                        </label>
                        {icon}
                    </FlexBox>
                </FlexBox>
            </BoxContent>
        </Box>
    );
}
interface PromoConfiguratorFAIProps {
    removeRadius?: boolean;
    isFullwidth?: boolean;
    marketingsState: MarketingsState;
    setMarketingsState: (marketings: PartialMarketingsState) => MarketingsState;
}

export function PromoConfiguratorFAI({
    marketingsState,
    setMarketingsState,
    ...props
}: Readonly<PromoConfiguratorFAIProps>) {
    return (
        <PromoConfigurator
            title='Voir les prix avec forfait mobile'
            description='Si vous avez ou prenez un forfait Bouygues Telecom.'
            isChecked={marketingsState[MarketingType.MOBILE]}
            onChange={({ switchState }) => {
                sendPromoConfiguratorTracking(switchState);
                setMarketingsState({ [MarketingType.MOBILE]: switchState });
            }}
            icon={
                <Popover
                    direction={PopoverDirection.LEFT}
                    trigger={<Icon name={IconName.INFOS_CIRCLE} size={IconSize.SMALL} />}
                >
                    Avec un forfait Bouygues Telecom de 20 Go ou plus (hors forfaits B&YOU) vous économisez chaque mois
                    sur votre box et sur votre forfait mobile.
                </Popover>
            }
            {...props}
        />
    );
}
